/**
 * Phase 5c FIX 4 — Floating chat side-sheet with history panel.
 *
 * - Always-visible bottom-right FAB (rendered globally, hidden on /login + /ai
 *   so it doesn't fight with the dedicated page).
 * - Click opens a right-anchored sheet (full-height) with the same ChatPanel.
 * - History icon in the header → slide-in panel (from the LEFT inside the
 *   sheet on desktop; full-screen takeover on mobile) listing recent convs.
 *   Switch threads or "+ New chat" without leaving the sheet.
 * - Last-active conversation_id persisted to localStorage; re-opening the
 *   sheet returns to the previous thread (until the user clicks New chat).
 */

import React, { useCallback, useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkle,
  X,
  ArrowSquareOut,
  ClockClockwise,
  Plus,
  ChatCircleDots,
  Trash,
  PushPin,
} from "@phosphor-icons/react";
import { toast } from "sonner";

import { useAuth } from "@/lib/auth";
import ChatPanel from "@/components/ChatPanel";
import {
  fetchConversations,
  deleteConversation as apiDeleteConversation,
  patchConversation as apiPatchConversation,
} from "@/lib/aiChat";
import { Skeleton } from "@/components/ui/skeleton";

const HIDDEN_PATHS = ["/login", "/ai"];
const LS_KEY = "reformity.floatingChat.lastConversationId";

function readLastConvId() {
  try {
    const v = window.localStorage?.getItem(LS_KEY);
    return v && v.length > 0 ? v : null;
  } catch (_) {
    return null;
  }
}

function writeLastConvId(id) {
  try {
    if (id) window.localStorage?.setItem(LS_KEY, id);
    else window.localStorage?.removeItem(LS_KEY);
  } catch (_) {
    /* noop */
  }
}

function formatRelative(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
    return d.toLocaleDateString("en-CA", {
      timeZone: "America/Toronto",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch (_) {
    return "";
  }
}

function HistoryPanel({
  open,
  onClose,
  activeId,
  onPick,
  onNewChat,
  refreshKey,
}) {
  const [convs, setConvs] = useState([]);
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const list = await fetchConversations();
      setConvs(list);
    } catch (_) {
      toast.error("Couldn't load conversations");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, refreshKey]);

  const onDelete = async (id, e) => {
    e?.stopPropagation();
    if (!window.confirm("Delete this conversation? This can't be undone.")) return;
    try {
      await apiDeleteConversation(id);
      toast.success("Conversation deleted");
      if (activeId === id) onPick(null);
      reload();
    } catch (_) {
      toast.error("Couldn't delete");
    }
  };

  const onPin = async (c, e) => {
    e?.stopPropagation();
    try {
      await apiPatchConversation(c.id, { pinned: !c.pinned });
      reload();
    } catch (_) {
      toast.error("Couldn't update");
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="history-panel"
          initial={{ x: "-100%" }}
          animate={{ x: 0 }}
          exit={{ x: "-100%" }}
          transition={{ type: "tween", duration: 0.22 }}
          className="absolute inset-0 z-10 bg-card flex flex-col"
          data-testid="floating-history-panel"
        >
          <header className="flex items-center justify-between px-4 py-3 border-b border-border">
            <div className="font-display font-bold text-sm">Recent chats</div>
            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-sm hover:bg-secondary text-muted-foreground"
              aria-label="Close history"
              data-testid="floating-history-close-btn"
            >
              <X size={16} weight="bold" />
            </button>
          </header>

          <div className="px-3 pt-3">
            <button
              type="button"
              onClick={() => {
                onNewChat();
                onClose();
              }}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-sm bg-[#F58216] hover:bg-[#F58216]/90 text-black text-sm font-medium transition-colors"
              data-testid="floating-history-new-chat-btn"
            >
              <Plus size={14} weight="bold" /> New chat
            </button>
          </div>

          <div className="flex-1 overflow-y-auto py-2 px-2">
            {loading ? (
              <div className="space-y-2 px-1">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : convs.length === 0 ? (
              <div className="px-4 py-8 text-center text-xs text-muted-foreground">
                No conversations yet.
              </div>
            ) : (
              <ul className="space-y-0.5">
                {convs.slice(0, 20).map((c) => {
                  const active = c.id === activeId;
                  return (
                    <li key={c.id}>
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                          onPick(c.id);
                          onClose();
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            onPick(c.id);
                            onClose();
                          }
                        }}
                        className={[
                          "group w-full flex items-start gap-2.5 px-3 py-2.5 rounded-sm cursor-pointer",
                          "transition-colors focus:outline-none focus:ring-2 focus:ring-[#F58216]/40",
                          active
                            ? "bg-[rgba(245,130,22,0.08)] border-l-2 border-[#F58216] pl-[10px]"
                            : "hover:bg-secondary border-l-2 border-transparent",
                        ].join(" ")}
                        data-testid={`floating-chat-history-row-${c.id}`}
                      >
                        <ChatCircleDots
                          size={16}
                          weight="duotone"
                          className={
                            active
                              ? "text-[#F58216] mt-0.5"
                              : "text-muted-foreground mt-0.5"
                          }
                        />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-foreground truncate">
                            {c.title || "New chat"}
                          </div>
                          <div className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-2">
                            <span>{c.message_count || 0} msgs</span>
                            <span>·</span>
                            <span>{formatRelative(c.updated_at)}</span>
                          </div>
                        </div>
                        <div className="opacity-0 group-hover:opacity-100 flex flex-col items-center gap-1 transition-opacity">
                          <button
                            type="button"
                            onClick={(e) => onPin(c, e)}
                            className="p-1 rounded-sm hover:bg-secondary text-muted-foreground hover:text-[#F58216]"
                            aria-label="Pin"
                            data-testid={`floating-conv-pin-${c.id}`}
                          >
                            <PushPin size={12} weight={c.pinned ? "fill" : "regular"} />
                          </button>
                          <button
                            type="button"
                            onClick={(e) => onDelete(c.id, e)}
                            className="p-1 rounded-sm hover:bg-secondary text-muted-foreground hover:text-rose-500"
                            aria-label="Delete"
                            data-testid={`floating-conv-delete-${c.id}`}
                          >
                            <Trash size={12} weight="bold" />
                          </button>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default function FloatingChat() {
  const { user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [conversationId, setConversationId] = useState(() => readLastConvId());
  const [historyOpen, setHistoryOpen] = useState(false);
  // bumped after each turn so the history panel reloads its list
  const [historyRefresh, setHistoryRefresh] = useState(0);

  // Close on route change (so the sheet doesn't sit open across page jumps).
  useEffect(() => {
    setOpen(false);
    setHistoryOpen(false);
  }, [location.pathname]);

  // Esc to close (history first, then sheet)
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (historyOpen) setHistoryOpen(false);
      else setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, historyOpen]);

  // Persist conv id to localStorage whenever it changes.
  useEffect(() => {
    writeLastConvId(conversationId);
  }, [conversationId]);

  const onNewChat = useCallback(() => {
    setConversationId(null);
  }, []);

  const onPickConv = useCallback((id) => {
    setConversationId(id);
  }, []);

  const onConversationCreated = useCallback((id) => {
    setConversationId(id);
  }, []);

  const onTurnDone = useCallback(() => {
    setHistoryRefresh((n) => n + 1);
  }, []);

  if (!user) return null;
  if (HIDDEN_PATHS.includes(location.pathname)) return null;

  return (
    <>
      {/* FAB */}
      <motion.button
        type="button"
        onClick={() => setOpen(true)}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className={[
          // Sit ABOVE the mobile bottom-nav (which is at bottom-16 h-16),
          // and ABOVE the "Made with Emergent" badge on desktop (the badge
          // is anchored at right-4 bottom-4 ~178×40 — without the lg:bottom-20
          // bump, headless Playwright .click() on this FAB gets intercepted
          // by the badge wrapper).
          "fixed right-4 bottom-36 lg:bottom-20 z-40",
          "h-12 w-12 grid place-items-center rounded-sm",
          "bg-[#F58216] hover:bg-[#F58216]/90 text-black",
          "shadow-lg shadow-black/30",
          "transition-shadow",
        ].join(" ")}
        aria-label="Open Reformity AI"
        data-testid="floating-chat-fab"
      >
        <Sparkle size={22} weight="fill" />
      </motion.button>

      <AnimatePresence>
        {open && (
          <>
            <motion.div
              key="overlay"
              className="fixed inset-0 bg-black/60 z-50"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
              data-testid="floating-chat-overlay"
            />
            <motion.aside
              key="sheet"
              className="fixed top-0 right-0 bottom-0 w-full sm:w-[420px] md:w-[480px] bg-card border-l border-border z-50 flex flex-col overflow-hidden"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "tween", duration: 0.25 }}
              data-testid="floating-chat-sheet"
            >
              <header className="flex items-center justify-between px-4 py-3 border-b border-border">
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className="h-8 w-8 rounded-sm bg-[#F58216] grid place-items-center shrink-0">
                    <Sparkle size={16} weight="fill" className="text-black" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-display font-bold text-sm leading-none">Reformity AI</div>
                    <div className="ref-overline mt-0.5 text-muted-foreground/70">
                      {user.role} · Sonnet 5
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setHistoryOpen((v) => !v)}
                    className={[
                      "p-2 rounded-sm hover:bg-secondary transition-colors",
                      historyOpen
                        ? "text-[#F58216] bg-secondary"
                        : "text-muted-foreground hover:text-[#F58216]",
                    ].join(" ")}
                    aria-label="Conversation history"
                    aria-pressed={historyOpen}
                    data-testid="floating-chat-history-btn"
                  >
                    <ClockClockwise size={16} weight="duotone" />
                  </button>
                  <button
                    type="button"
                    onClick={onNewChat}
                    className="p-2 rounded-sm hover:bg-secondary text-muted-foreground hover:text-[#F58216] transition-colors"
                    aria-label="New chat"
                    data-testid="floating-chat-new-btn"
                  >
                    <Plus size={16} weight="bold" />
                  </button>
                  <Link
                    to="/ai"
                    onClick={() => setOpen(false)}
                    className="p-2 rounded-sm hover:bg-secondary text-muted-foreground hover:text-[#F58216] transition-colors"
                    aria-label="Open full chat page"
                    data-testid="floating-chat-expand-btn"
                  >
                    <ArrowSquareOut size={16} weight="duotone" />
                  </Link>
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="p-2 rounded-sm hover:bg-secondary text-muted-foreground"
                    aria-label="Close"
                    data-testid="floating-chat-close-btn"
                  >
                    <X size={16} weight="bold" />
                  </button>
                </div>
              </header>
              {/* Body — chat panel; history panel overlays this on demand */}
              <div className="flex-1 min-h-0 relative">
                <ChatPanel
                  conversationId={conversationId}
                  onConversationCreated={onConversationCreated}
                  onTurnDone={onTurnDone}
                  testIdPrefix="floating"
                  compact
                />
                <HistoryPanel
                  open={historyOpen}
                  onClose={() => setHistoryOpen(false)}
                  activeId={conversationId}
                  onPick={onPickConv}
                  onNewChat={onNewChat}
                  refreshKey={historyRefresh}
                />
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
